import { z } from 'zod';
import {
  DateTimeSchema,
  DurationLikeObject,
  DurationSchema,
  ObjectIdString,
} from '@repo/common/schemas';
import { PromptType } from '@repo/common/schemas/llm';
import {
  ActivityDataEntityType,
  GenerateInsightsOptions,
  GetActivityDataOptions,
  RefreshActivityDataOptions,
} from '@repo/common/schemas/api';
import { RecruiterDTO, UserDTO } from '@repo/common/schemas/models';

export const ErrorResponse = z.looseObject({
  error: z.string(),
  message: z.string().optional(),
  stack: z.string().optional(),
  cause: z.unknown().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const LookupContactParams = z.object({
  _id: ObjectIdString.optional(),
  accountId: ObjectIdString.optional(),
  sourceId: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),

  lookupRelatedEntities: z.boolean().optional(),
  lookupStart: z.union([DateTimeSchema, DurationSchema]).optional(),
});
export type LookupContactParams = z.infer<typeof LookupContactParams>;

export const LookupCandidateParams = z.object({
  ...LookupContactParams.shape,
  corpId: z.string().optional(),
  candidateId: z.string().optional(),
  email: z.string().optional(),

  forceRefresh: z.boolean().optional(),
  cacheOnly: z.boolean().optional(),
});
export type LookupCandidateParams = z.infer<typeof LookupCandidateParams>;

/** @deprecated, use GetCandidateActivityParams */
export const CandidateActivityParams = z.object({
  ...LookupCandidateParams.shape,
  ...GetActivityDataOptions.shape,
  // lookBackUnit: z.string().optional(),
  // lookBackNumber: z.number().optional(),
});
/** @deprecated */
export type CandidateActivityParams = z.infer<typeof CandidateActivityParams>;

export const PromptQuery = z.object({
  promptId: ObjectIdString.optional(),
  promptName: z.string().optional(),
  promptType: z.string().optional(),
  testPromptData: z.looseObject({}).optional(),
});
export type PromptQuery = z.infer<typeof PromptQuery>;

export const ToroCustomHeader = z.enum(['toro-user-id', 'toro-session-id']);
export type ToroCustomHeader = z.infer<typeof ToroCustomHeader>;

export const EMBEDDED_SESSION_COOKIE = 'toro-embedded-session';
export const ToroCustomCookie = z.enum([EMBEDDED_SESSION_COOKIE]);
export type ToroCustomCookie = z.infer<typeof ToroCustomCookie>;

export const GetCandidateActivityParams = z.object({
  candidateId: z.string().describe('Candidate _id or sourceId'),
  ...GetActivityDataOptions.shape,
});
export type GetCandidateActivityParams = z.infer<typeof GetCandidateActivityParams>;

export const GetEntityActivityParams = z.object({
  entityType: ActivityDataEntityType,
  entityId: z.string().describe('entity _id or sourceId'),
  ...GetActivityDataOptions.shape,
});
export type GetContactActivityParams = z.infer<typeof GetEntityActivityParams>;

export const RefreshCandidateActivityParams = z.object({
  ...GetCandidateActivityParams.shape,
  ...RefreshActivityDataOptions.shape,
});
export type RefreshCandidateActivityParams = z.infer<typeof RefreshCandidateActivityParams>;

export const RefreshEntityActivityParams = z.object({
  ...GetEntityActivityParams.shape,
  ...RefreshActivityDataOptions.shape,
});
export type RefreshEntityActivityParams = z.infer<typeof RefreshEntityActivityParams>;

export const CachedInsightsParams = z.object({
  promptType: PromptType,
  promptId: ObjectIdString.optional().describe('Prompt ID override'),
  candidateId: z.string().describe('Candidate _id or sourceId'),
});
export type CachedInsightsParams = z.infer<typeof CachedInsightsParams>;

export const GenerateInsightsParams = z.object({
  ...CachedInsightsParams.shape,
  opportunityId: ObjectIdString.optional(),
  options: GenerateInsightsOptions.optional(),
});
export type GenerateInsightsParams = z.infer<typeof GenerateInsightsParams>;

export const UserIdentityDTO = z.object({
  ...UserDTO.shape,
  hasLinkedRecruiter: z.boolean().default(false),
  departmentKey: z.string().array().nullable().default(null),
  accountName: z.string(),
  linkedRecruiter: RecruiterDTO.nullish(),
});
export type UserIdentityDTO = z.infer<typeof UserIdentityDTO>;

export const ExtensionUserSessionInfo = UserIdentityDTO.pick({
  email: true,
  name: true,
  accountName: true,
}).partial();
export type ExtensionUserSessionInfo = z.infer<typeof ExtensionUserSessionInfo>;
